import { useState } from "react";
import type { CounterPrinted, Settlement, Statement } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { counterToast } from "../printing/print-notices";
import { CourierBon } from "./CourierBon";

interface Props {
  statement: Statement | Settlement;
  /** When the bon was asked for; a settlement prints the moment it was settled. */
  at: string;
  onClose: () => void;
}

/**
 * "Afrekenbon · Mehmet" (prototype openCourierBon): before settling, printed without a code so the
 * courier can check it and get his money ready; after settling, the copy. "🖨 Afdrukken" prints it on
 * the printer of the Z-rapport ("Afrekenbon Mehmet → Kassaprinter toog"); a shop without that printer
 * prints from the browser (80 mm).
 */
export function CourierBonDialog({ statement, at, onClose }: Props) {
  const { api, general, status, toast, report } = useTill();
  const [busy, setBusy] = useState(false);
  const text = t.couriers.bonDialog;
  const settlement = "settledAt" in statement ? statement : null;

  const print = async () => {
    setBusy(true);
    try {
      const path =
        settlement === null
          ? `/device/couriers/${statement.courier.id}/statement/print`
          : `/device/settlements/${settlement.id}/print`;
      const answer = await api.request<{ printed: CounterPrinted }>("POST", path);
      const said = counterToast(answer.printed);
      if (said === null) window.print();
      else toast(said);
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={text.title(statement.courier.name)}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" disabled={busy} onClick={() => void print()}>
            {text.print}
          </button>
        </>
      }
    >
      <div className="bonwrap zbonpanel">
        <CourierBon
          shop={general?.receiptName ?? status.shop.name}
          statement={statement}
          at={settlement?.settledAt ?? at}
        />
      </div>
      <p className="note">{settlement === null ? text.pendingNote : text.copy}</p>
    </Dialog>
  );
}
